(function () {
  if (!customElements.get('location-gate')) {
    var LOCATION_RESOLVED_KEY = 'locationGate:resolved';

    // Inerts every element under <body> except `allowedEl` (and its
    // ancestors), leaving the rest of the page unusable until the shopper
    // deals with it. Mirrors snippets/cookie-consent-gate.liquid, but scoped
    // to a single nested element instead of body's direct children, since
    // the location gate lives inside <main> alongside every other homepage
    // section.
    function lockPageExcept(allowedEl, locked) {
      function walk(node) {
        Array.from(node.children).forEach(function (child) {
          if (child === allowedEl || child.contains(allowedEl)) {
            child.removeAttribute('inert');
            if (child !== allowedEl) walk(child);
            return;
          }
          if (locked) {
            child.setAttribute('inert', '');
          } else {
            child.removeAttribute('inert');
          }
        });
      }
      walk(document.body);
    }

    customElements.define(
      'location-gate',
      class LocationGate extends HTMLElement {
        constructor() {
          super();
          this.toggle = this.querySelector('.location-gate__toggle');
          this.panel = this.querySelector('.location-gate__list-wrapper');
          this.options = Array.from(this.querySelectorAll('.location-gate__option'));
          this.countryPanel = this.querySelector('.location-gate__country-panel');
          this.countryOptions = Array.from(this.querySelectorAll('.location-gate__country'));
          this.confirmButton = this.querySelector('.location-gate__confirm');
          this.countryInput = this.querySelector('input[name="country_code"]');
          this.currentLabel = this.querySelector('[data-location-gate-current]');
          this.currentCountryLabel = this.querySelector('[data-location-gate-current-country]');
          this.initialCountry = this.countryInput.value;
          this.pending = false;

          this.toggle.addEventListener('click', this.onToggleClick.bind(this));
          this.options.forEach((option) => option.addEventListener('click', this.onOptionClick.bind(this)));
          this.countryOptions.forEach((option) => option.addEventListener('click', this.onCountryClick.bind(this)));
          this.confirmButton.addEventListener('click', this.onConfirmClick.bind(this));
          this.addEventListener('focusout', this.onFocusOut.bind(this));
          this.addEventListener('keyup', this.onKeyUp.bind(this));
        }

        connectedCallback() {
          this.initMandatoryLock();
        }

        initMandatoryLock() {
          if (window.Shopify && window.Shopify.designMode) {
            // Theme editor preview: don't force the country/location picker
            // on whoever is editing the theme.
            this.hide();
            return;
          }
          var resolvedBefore = false;
          try {
            resolvedBefore = window.localStorage.getItem(LOCATION_RESOLVED_KEY) === '1';
          } catch (error) {
            // Storage unavailable: treated as "not resolved yet" below, same
            // as a first-ever visit — the picker just won't remember the
            // choice across sessions in that case.
          }
          if (resolvedBefore) {
            // Already dealt with on a previous visit: the whole widget goes
            // away rather than sitting there forever as a "Select your
            // location: Europe ▾" fixture.
            this.hide();
            return;
          }

          var engage = this.engageMandatoryLock.bind(this);
          if (document.documentElement.classList.contains('cookie-consent-resolved')) {
            engage();
          } else {
            // Cookie consent still pending: wait for it to resolve first, so
            // the two gates don't fight over the page's `inert` state.
            document.addEventListener('cookieConsentGate:resolved', engage, { once: true });
          }
        }

        engageMandatoryLock() {
          // Mandatory just means the shopper can't dismiss/ignore it without
          // picking something — it does not mean force the panel open. It
          // starts collapsed (toggle + caret, see the `hidden` attribute
          // already on the panel in the markup) like a normal disclosure;
          // the shopper opens it themselves via the toggle.
          this.pending = true;
          document.documentElement.classList.add('location-gate-pending');
          lockPageExcept(this, true);
        }

        resolveMandatoryLock() {
          if (!this.pending) return;
          this.pending = false;
          document.documentElement.classList.remove('location-gate-pending');
          lockPageExcept(this, false);
        }

        markResolved() {
          try {
            window.localStorage.setItem(LOCATION_RESOLVED_KEY, '1');
          } catch (error) {
            // Storage unavailable: nothing to persist, the picker will
            // simply ask again next visit.
          }
        }

        hide() {
          document.documentElement.classList.add('location-gate-resolved');
        }

        onToggleClick() {
          const expanded = this.toggle.getAttribute('aria-expanded') === 'true';
          // Mandatory: can open it, but can't collapse it again without
          // resolving first (still has to pick something).
          if (this.pending && expanded) return;
          this.toggle.setAttribute('aria-expanded', (!expanded).toString());
          this.panel.toggleAttribute('hidden', expanded);
          // Lets the hero video/header dim and hide while the shopper is
          // actively picking a region, the same way they do while the
          // cookie consent banner (or the mandatory first-visit lock) is
          // pending.
          document.documentElement.classList.toggle('location-gate-open', !expanded);
        }

        onOptionClick(event) {
          const option = event.currentTarget;
          this.options.forEach((item) => {
            const isSelected = item === option;
            item.classList.toggle('location-gate__option--current', isSelected);
            if (isSelected) {
              item.setAttribute('aria-current', 'true');
            } else {
              item.removeAttribute('aria-current');
            }
          });
          if (this.currentLabel) this.currentLabel.textContent = option.textContent.trim();

          if (option.dataset.region === 'row' && this.countryPanel) {
            // "Rest of the world" spans many markets: list every available
            // country directly instead of silently picking a representative
            // one. Staged only — nothing submits until "Confirmar" is
            // pressed below.
            this.countryPanel.hidden = false;
            const preselected = this.countryPanel.querySelector('[aria-current="true"]');
            if (preselected) {
              this.countryInput.value = preselected.dataset.country;
              if (this.currentCountryLabel) this.currentCountryLabel.textContent = preselected.textContent.trim();
            } else {
              if (this.currentCountryLabel) this.currentCountryLabel.textContent = '';
            }
            return;
          }

          if (this.countryPanel) this.countryPanel.hidden = true;
          this.countryInput.value = option.dataset.country;
        }

        onCountryClick(event) {
          const button = event.currentTarget;
          this.countryOptions.forEach((item) => {
            const isSelected = item === button;
            item.classList.toggle('location-gate__option--current', isSelected);
            if (isSelected) {
              item.setAttribute('aria-current', 'true');
            } else {
              item.removeAttribute('aria-current');
            }
          });
          this.countryInput.value = button.dataset.country;
          if (this.currentCountryLabel) this.currentCountryLabel.textContent = button.textContent.trim();
        }

        onConfirmClick(event) {
          this.markResolved();

          if (this.countryInput.value === this.initialCountry) {
            // Nothing actually changed (the shopper just confirmed the
            // detected default): no need for a real form submit/page
            // reload, just close the picker, unlock the rest of the page,
            // and make the whole widget disappear.
            event.preventDefault();
            this.resolveMandatoryLock();
            this.toggle.setAttribute('aria-expanded', 'false');
            this.panel.setAttribute('hidden', '');
            document.documentElement.classList.remove('location-gate-open');
            this.hide();
            return;
          }

          // Otherwise let the form submit for real: it reloads the page
          // with the new country/currency. `locationGate:resolved`,
          // already set above, carries over so the picker won't force
          // itself open again.
        }

        onKeyUp(event) {
          if (this.pending) return;
          if (event.key === 'Escape' && this.toggle.getAttribute('aria-expanded') === 'true') {
            this.onToggleClick();
            this.toggle.focus();
          }
        }

        onFocusOut(event) {
          if (this.pending) return;
          if (!this.contains(event.relatedTarget)) {
            this.toggle.setAttribute('aria-expanded', 'false');
            this.panel.setAttribute('hidden', '');
            document.documentElement.classList.remove('location-gate-open');
          }
        }
      }
    );
  }
})();
